import { apiFetch } from "../lib/api";
import { HierarchyAccessError, id, object, request } from "./api";

export interface AgentProfile { id: string; revision: string; name: string }
export interface AgentAssignment { id: string; previousId?: string; profile: AgentProfile }
export interface PendingAssignment { id: string; previousId?: string; profileId: string; profileRevision: string }
export interface AgentChoices { profiles: AgentProfile[]; current?: AgentAssignment; pending?: PendingAssignment }

export function profile(raw: unknown): AgentProfile {
  const value = object(raw);
  if (typeof value.revision !== "string" || !/^sha256:[a-f0-9]{64}$/.test(value.revision) ||
    typeof value.name !== "string" || !value.name.trim() || value.name.length > 512) throw new HierarchyAccessError();
  return { id: id(value.id), revision: value.revision, name: value.name };
}

export function assignment(raw: unknown): AgentAssignment {
  const value = object(raw);
  return { id: id(value.id), previousId: value.previousId === undefined || value.previousId === "" ? undefined : id(value.previousId), profile: profile(value.profile) };
}

export function pendingAssignment(raw: unknown): PendingAssignment {
  const value = object(raw);
  if (Object.keys(value).some((key) => !["id", "previousId", "profileId", "profileRevision"].includes(key))) throw new HierarchyAccessError();
  const selected = profile({ id: value.profileId, revision: value.profileRevision, name: "pending" });
  const result = { id: id(value.id), previousId: value.previousId === undefined ? undefined : id(value.previousId), profileId: selected.id, profileRevision: selected.revision };
  if (result.id === result.previousId) throw new HierarchyAccessError();
  return result;
}

export async function currentAgent(root: string, signal?: AbortSignal): Promise<AgentAssignment | undefined> {
  const response = await apiFetch(`${root}/assignment`, { credentials: "include", cache: "no-store", signal });
  if (response.status === 404) { await response.body?.cancel(); signal?.throwIfAborted(); return undefined; }
  if (!response.ok) throw new HierarchyAccessError();
  const result = assignment(await response.json());
  signal?.throwIfAborted();
  return result;
}

export async function agentProfiles(accountId: string, workspaceId: string, signal?: AbortSignal): Promise<AgentProfile[]> {
  const profiles: AgentProfile[] = [];
  const seen = new Set<string>();
  const cursors = new Set<string>();
  let after = "";
  do {
    const page = await request(`/code/workspaces/${encodeURIComponent(workspaceId)}/agent-profiles${after ? `?after=${encodeURIComponent(after)}` : ""}`, signal);
    if (page.accountId !== accountId || page.workspaceId !== workspaceId || !Array.isArray(page.profiles) || page.profiles.length > 50) throw new HierarchyAccessError();
    for (const raw of page.profiles) {
      const item = profile(raw);
      if (seen.has(item.id)) throw new HierarchyAccessError();
      seen.add(item.id); profiles.push(item);
    }
    const next = page.nextCursor === undefined || page.nextCursor === "" ? "" : id(page.nextCursor);
    if (next && (cursors.has(next) || next !== profiles.at(-1)?.id || page.profiles.length !== 50)) throw new HierarchyAccessError();
    if (next) cursors.add(next);
    after = next;
    signal?.throwIfAborted();
  } while (after);
  return profiles;
}
